// Dynamic Popup States - Schema-based approach
// This replaces the hardcoded popupType system with a completely dynamic one
import { isEffectivelyEmbeddedPopup } from "../../utils/cartDrawerRuntime";

/**
 * Generate dynamic popup state based on template schema
 * @param {Object} popupData - The popup data with schema
 * @param {boolean} isPreviewMode - Whether we're in preview mode
 * @returns {Object} - The popup state object
 */
export const generateDynamicPopupState = (popupData, isPreviewMode = false) => {
  // Default state for any template
  const defaultState = {
    activeStep: 0,
    openPopup: true, // Always start with popup open in preview mode
    isEmbeded: false,
    isAiRecommend: false,
    aiRecommendedProduct: null,
  };

  // If no popup data, return default
  if (!popupData) {
    return defaultState;
  }

  // Check if this is a draggable template
  const isDraggable = popupData?.generalSettings?.isDraggable;
  
  if (isDraggable) {
    // For draggable templates, use schema-based logic
    return generateDraggablePopupState(popupData, isPreviewMode);
  } else {
    // For non-draggable templates, use legacy logic but make it dynamic
    return generateLegacyPopupState(popupData, isPreviewMode);
  }
};

/**
 * Generate state for draggable templates based on schema
 */
const generateDraggablePopupState = (popupData, isPreviewMode) => {
  const schema = popupData?.schema || popupData?.default_schema?.schema;
  
  if (!schema || !Array.isArray(schema) || schema.length === 0) {
    return {
      activeStep: 0,
      openPopup: true,
      isEmbeded: false,
    };
  }

  // Determine active step based on schema structure
  let activeStep = 0;
  
  // If there are multiple steps, find the first one with content
  if (schema.length > 1) {
    for (let i = 0; i < schema.length; i++) {
      const step = schema[i];
      if (step?.section && step.section.length > 0) {
        activeStep = i;
        break;
      }
    }
  }

  // Determine if popup should be open based on schema analysis
  const shouldBeOpen = determinePopupOpenState(popupData, isPreviewMode);

  return {
    activeStep,
    openPopup: shouldBeOpen,
    isEmbeded: isEffectivelyEmbeddedPopup(popupData),
    isAiRecommend: false,
    aiRecommendedProduct: null,
  };
};

/**
 * Generate state for legacy non-draggable templates
 */
const generateLegacyPopupState = (popupData, isPreviewMode) => {
  // For legacy templates, we'll use a more intelligent approach
  const templateType = popupData?.popUpType || popupData?.template_type;
  
  // Analyze the template type to determine behavior
  const analysis = analyzeTemplateType(templateType);
  
  return {
    activeStep: analysis.defaultStep || 0,
    openPopup: isPreviewMode ? true : analysis.shouldStartOpen,
    isEmbeded: isEffectivelyEmbeddedPopup(popupData),
    isAiRecommend: false,
    aiRecommendedProduct: null,
  };
};

/**
 * Determine if popup should be open based on template analysis
 */
const determinePopupOpenState = (popupData, isPreviewMode) => {
  // In preview mode, always show popup
  if (isPreviewMode) {
    return true;
  }

  const schema = popupData?.schema || popupData?.default_schema?.schema;
  const templateType = popupData?.popUpType || popupData?.template_type;
  
  // Analyze template content to determine behavior
  const analysis = analyzeTemplateContent(schema, templateType);
  
  return analysis.shouldStartOpen;
};

/**
 * Analyze template type to determine default behavior
 */
const analyzeTemplateType = (templateType) => {
  const type = (templateType || '').toLowerCase();
  
  // Social proof templates typically start open
  if (type.includes('social') || type.includes('proof') || type.includes('recent')) {
    return {
      defaultStep: 0,
      shouldStartOpen: true,
      isEmbeded: false,
    };
  }
  
  // Countdown templates typically start open
  if (type.includes('countdown') || type.includes('timer') || type.includes('limited')) {
    return {
      defaultStep: 0,
      shouldStartOpen: true,
      isEmbeded: false,
    };
  }
  
  // Video templates typically start open
  if (type.includes('video') || type.includes('shoppable')) {
    return {
      defaultStep: 0,
      shouldStartOpen: true,
      isEmbeded: type.includes('slider'),
    };
  }
  
  // Welcome templates typically start closed
  if (type.includes('welcome') || type.includes('discount') || type.includes('offer')) {
    return {
      defaultStep: 2,
      shouldStartOpen: false,
      isEmbeded: false,
    };
  }
  
  // Upsell templates typically start closed
  if (type.includes('upsell') || type.includes('bundle') || type.includes('volume')) {
    return {
      defaultStep: 1,
      shouldStartOpen: false,
      isEmbeded: false,
    };
  }
  
  // Default behavior
  return {
    defaultStep: 0,
    shouldStartOpen: false,
    isEmbeded: false,
  };
};

/**
 * Analyze template content to determine behavior
 */
const analyzeTemplateContent = (schema, templateType) => {
  if (!schema || !Array.isArray(schema)) {
    return { shouldStartOpen: false };
  }
  
  // Look for specific section types that indicate behavior
  const sectionTypes = [];
  schema.forEach(step => {
    if (step?.section) {
      step.section.forEach(section => {
        if (section?.type) {
          sectionTypes.push(section.type);
        }
      });
    }
  });
  
  // Social proof indicators
  if (sectionTypes.includes('ORDERS_DISPLAY') || 
      sectionTypes.includes('TEST_ORDERS') || 
      sectionTypes.includes('VERIFICATION')) {
    return { shouldStartOpen: true };
  }
  
  // Countdown indicators
  if (sectionTypes.includes('COUNTDOWN') || 
      sectionTypes.includes('TIMER')) {
    return { shouldStartOpen: true };
  }
  
  // Video indicators
  if (sectionTypes.includes('VIDEO') || 
      sectionTypes.includes('VIDEO_SLIDER')) {
    return { shouldStartOpen: true };
  }
  
  // Welcome/discount indicators
  if (sectionTypes.includes('DISCOUNT_CODE') || 
      sectionTypes.includes('BIRTHDAY') || 
      sectionTypes.includes('INPUT')) {
    return { shouldStartOpen: false };
  }
  
  // Default behavior
  return { shouldStartOpen: false };
};

/**
 * Get popup state for a specific template
 * This replaces the old hardcoded mapping system
 */
export const getPopupState = (popupData, isPreviewMode = false) => {
  return generateDynamicPopupState(popupData, isPreviewMode);
};

/**
 * Check if popup should close based on template analysis
 * This replaces the old shouldClosePopup function
 */
export const shouldClosePopup = (popupData) => {
  const analysis = analyzeTemplateType(popupData?.popUpType || popupData?.template_type);
  return !analysis.shouldStartOpen;
};

/**
 * Get default state for any template type
 * This is a fallback for backward compatibility
 */
export const getDefaultState = (popupData, isPreviewMode = false) => {
  return generateDynamicPopupState(popupData, isPreviewMode);
};


